import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { CopilotStore } from '../CopilotStore';
import { PartImageIngestResponse } from '../models';
import StepConfirmation from '../../controls/StepConfirmation';

type PartsManagementProps = {
    store: CopilotStore
}

type DeleteAction = {
    label: string
    warning: string
    run: (onProgress: (done: number, total: number) => void) => Promise<{ deleted: number, total: number }>
    noun: string
}

export const PartsManagement = observer(({ store }: PartsManagementProps) => {
    const { selectedCustomer: customer } = store

    const [reingestExisting, setReingestExisting] = useState(false)
    const [busy, setBusy] = useState<string>()
    const [progress, setProgress] = useState<{ done: number, total: number }>()
    const [ingestResult, setIngestResult] = useState<PartImageIngestResponse>()
    const [message, setMessage] = useState<string>()
    const [error, setError] = useState<string>()

    // The dialog only unlocks once the tenant name is typed back exactly.
    const [pending, setPending] = useState<DeleteAction>()
    const [typedName, setTypedName] = useState('')

    useEffect(() => {
        setIngestResult(undefined)
        setMessage(undefined)
        setError(undefined)
    }, [customer])

    // ponytail: FastAPI 422 sends `detail` as an array of objects, which React refuses to render.
    const describeError = (e: any) => {
        const detail = e?.response?.data?.detail ?? e?.message
        if (typeof detail === 'string') return detail
        return detail ? JSON.stringify(detail) : 'Request failed'
    }

    const ingest = async () => {
        setBusy('ingest')
        setIngestResult(undefined)
        setMessage(undefined)
        setError(undefined)
        try {
            setIngestResult(await store.ingestAllPartImages(reingestExisting))
        } catch (e: any) {
            setError(describeError(e))
        } finally {
            setBusy(undefined)
        }
    }

    const closeDialog = () => {
        setPending(undefined)
        setTypedName('')
    }

    const runPending = async () => {
        const action = pending!
        closeDialog()
        setBusy(action.label)
        setProgress(undefined)
        setIngestResult(undefined)
        setMessage(undefined)
        setError(undefined)
        try {
            const { deleted, total } = await action.run((done, t) => setProgress({ done, total: t }))
            setMessage(total === 0 ? `No ${action.noun} to delete.` : `Deleted ${deleted} of ${total} ${action.noun}.`)
        } catch (e: any) {
            setError(describeError(e))
        } finally {
            setBusy(undefined)
            setProgress(undefined)
        }
    }

    const deleteImages: DeleteAction = {
        label: 'Delete all part images',
        warning: 'Every part image of this tenant will be deleted. The parts themselves stay.',
        noun: 'images',
        run: store.deleteAllPartImages,
    }

    const deleteParts: DeleteAction = {
        label: 'Delete all parts',
        warning: 'Every part of this tenant will be deleted, along with all of its images.',
        noun: 'parts',
        run: store.deleteAllParts,
    }

    if (!customer) return <Typography>Select a customer to manage parts.</Typography>

    const spinner = <CircularProgress size={16} color="inherit" />

    return (
        <Stack spacing={2} alignItems="flex-start" padding={1}>
            <Typography>
                Embeds every part image of <b>{customer.name}</b>. Runs in the background - the queued
                jobs keep going after you leave this page.
            </Typography>
            <FormControlLabel
                control={<Checkbox
                    checked={reingestExisting}
                    onChange={e => setReingestExisting(e.target.checked)}
                    disabled={!!busy}
                />}
                label="Re-ingest existing images (re-embeds and replaces vectors that already exist)"
            />
            <Button variant="contained" onClick={ingest} disabled={!!busy}
                startIcon={busy === 'ingest' ? spinner : undefined}>
                {busy === 'ingest' ? 'Queueing…' : 'Ingest all part images'}
            </Button>
            {ingestResult && (ingestResult.count > 0
                ? <Alert severity="success">Queued {ingestResult.count} images (group {ingestResult.group_id}).</Alert>
                : <Alert severity="info">No part images found for this tenant.</Alert>)}

            <Divider flexItem />

            <Typography>Deletion is permanent - you will be asked to type the tenant name to confirm.</Typography>
            <Stack direction="row" spacing={2}>
                {[deleteImages, deleteParts].map(action => (
                    <Button key={action.label} variant="outlined" color="error" disabled={!!busy}
                        onClick={() => { setTypedName(''); setPending(action) }}
                        startIcon={busy === action.label ? spinner : undefined}>
                        {action.label}
                    </Button>
                ))}
            </Stack>
            {busy && busy !== 'ingest' &&
                <Typography>{progress ? `Deleting ${progress.done}/${progress.total}…` : 'Collecting parts…'}</Typography>}
            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            {pending && <StepConfirmation
                isValid={typedName.trim() === customer.name}
                finish={runPending}
                cancel={closeDialog}
                confirmationDialogs={[{
                    title: pending.label,
                    onClose: () => {},
                    onYes: () => {},
                    yesLabel: 'Delete',
                    yesDisabled: typedName.trim() !== customer.name,
                    closeLabel: 'Cancel',
                    content: <Stack spacing={2}>
                        <Typography>{pending.warning} This cannot be undone.</Typography>
                        <Typography>Type <b>{customer.name}</b> to continue:</Typography>
                        <TextField
                            autoFocus
                            size="small"
                            value={typedName}
                            onChange={e => setTypedName(e.target.value)}
                            inputProps={{ 'aria-label': 'tenant name confirmation' }}
                        />
                    </Stack>
                }]}
            />}
        </Stack>
    )
})
